import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { t } from '../i18n';
import { useLocale } from '../i18n/useT';
import { localizeError } from '../i18n/errors';
import { useCurrency } from '../utils/currencyContext';
import { calculateSettlements } from '../utils/pokerLogic';
import { settlementEntries, voidUnfinishedHand } from '../utils/playerLedger';
import { formatSignedCurrency, settlementRecap } from '../utils/settlementRecap';
import { submitFeedback } from '../utils/feedback';
import Wordmark from './ui/Wordmark';

// Ask for a rating again after this long — every-game prompting is nagging,
// never-again starves feedback from the most engaged (repeat) players.
const RATING_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const RATED_AT_KEY = 'chipless:fb:rated_at';

function hasRatedRecently() {
  try {
    const ratedAt = Number(localStorage.getItem(RATED_AT_KEY));
    return ratedAt > 0 && Date.now() - ratedAt < RATING_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markRated() {
  try {
    localStorage.setItem(RATED_AT_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable — worst case the card shows again next game.
  }
}

// Inline end-game feedback card. Thumb tap submits the rating IMMEDIATELY
// (so it's captured even if they leave right after), then the card transforms
// in place into an optional "Anything else?" follow-up with a reply email
// field and the direct mailto line. The game is ENDED here, so nothing —
// no next hand, no host action — can interrupt someone mid-typing.
function FeedbackCard() {
  // 'idle' → thumbs question; 'rated' → optional text follow-up; 'done' → thanks.
  const [phase, setPhase] = useState(() => (hasRatedRecently() ? 'hidden' : 'idle'));
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (phase === 'hidden') return null;

  const handleRate = (value) => {
    if (rating) return;
    setRating(value);
    setPhase('rated');
    markRated();
    // Fire-and-forget: the tap is the submission. The optional text below is
    // a separate entry, so a failure here must not block the follow-up UI.
    submitFeedback({ rating: value, context: { source: 'end_game' } }).catch(() => {});
  };

  const handleSendFollowUp = async (e) => {
    e?.preventDefault();
    if (sending || !message.trim()) return;
    setSending(true);
    setError('');
    try {
      await submitFeedback({ message, email, context: { source: 'end_game_followup' } });
      setPhase('done');
    } catch (err) {
      setError(localizeError(err?.message || 'Something went wrong. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    'w-full px-3.5 py-2.5 bg-transparent border border-line rounded-lg text-[14px] text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors';

  return (
    <div className="px-6 sm:px-8 pt-7">
      <div className="rounded-xl border border-line px-4 py-4">
        <div className="label-eyebrow text-mute">{t('settle.feedbackTitle')}</div>

        {phase === 'idle' && (
          <div className="mt-2.5 flex items-center justify-between gap-4">
            <p className="text-[15px] text-ink m-0">{t('settle.howWas')}</p>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => handleRate('up')}
                aria-label={t('settle.thumbsUp')}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                className="w-12 h-12 rounded-full border border-line flex items-center justify-center text-ink hover:bg-ink hover:text-cream hover:border-ink transition-colors"
              >
                <ThumbsUp size={20} strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => handleRate('down')}
                aria-label={t('settle.thumbsDown')}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                className="w-12 h-12 rounded-full border border-line flex items-center justify-center text-ink hover:bg-ink hover:text-cream hover:border-ink transition-colors"
              >
                <ThumbsDown size={20} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        )}

        {phase === 'rated' && (
          <form onSubmit={handleSendFollowUp} className="mt-2.5 space-y-3">
            <div className="flex items-center gap-2 text-ink">
              {rating === 'up'
                ? <ThumbsUp size={16} strokeWidth={2} />
                : <ThumbsDown size={16} strokeWidth={2} />}
              <p className="text-[15px] font-medium m-0">{t('settle.thanks')}</p>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('settle.anythingElse')}
              className={`${inputClass} min-h-[76px] resize-y`}
              maxLength={2000}
              disabled={sending}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('settle.emailReply')}
              className={inputClass}
              maxLength={254}
              disabled={sending}
            />
            {error && (
              <div className="text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <p className="text-[12px] text-mute m-0">
                {t('settle.orEmail')}{' '}
                <a
                  href="mailto:playchipless@outlook.com"
                  className="text-ink underline underline-offset-2 decoration-line hover:decoration-ink transition-colors"
                >
                  playchipless@outlook.com
                </a>
              </p>
              <button
                type="submit"
                disabled={sending || !message.trim()}
                className="px-4 py-2 rounded-full bg-ink text-cream text-[13px] font-semibold disabled:opacity-40 transition-opacity"
              >
                {sending ? t('common.sendingDots') : t('common.send')}
              </button>
            </div>
          </form>
        )}

        {phase === 'done' && (
          <p className="mt-2.5 text-[15px] text-ink m-0">
            {t('settle.thankYou')}
          </p>
        )}
      </div>
    </div>
  );
}

const EndGameScreen = ({ gameState, onBackToHome }) => {
  useLocale();
  const { formatCurrency, currency, locale } = useCurrency();
  const { players = {} } = gameState;

  const playersWithInvestment = settlementEntries(voidUnfinishedHand(players).players);

  const { transactions, playersWithResults, hasSettlements } = calculateSettlements(playersWithInvestment);
  const recap = settlementRecap(playersWithResults);
  const { ranked: sorted, winner, headlineName, headlineRest } = recap;

  return (
    <div className="min-h-[100dvh] bg-cream text-ink flex flex-col overflow-y-auto" data-testid="end-game-screen">

      {/* Header — wordmark + playchipless.com */}
      <header className="flex items-center justify-between px-6 pt-[54px] sm:px-8">
        <Wordmark />
        <span className="text-[13px] text-mute">playchipless.com</span>
      </header>

      {/* Hero */}
      <div className="px-[26px] pt-10 pb-[36px]">
        <h1 className="text-[56px] leading-[0.95] tracking-[-0.038em] m-0 text-ink">
          {headlineName ? (
            <>
              <span style={{ fontWeight: 600 }}>{headlineName}</span>
              <br />
              <span style={{ fontWeight: 400 }}>{headlineRest[0]}</span>
              <br />
              <span style={{ fontWeight: 400 }}>{headlineRest[1]}</span>
            </>
          ) : (
            <>
              <span style={{ fontWeight: 600 }}>{headlineRest[0]}</span>
              <br />
              <span style={{ fontWeight: 400 }}>{headlineRest[1]}</span>
            </>
          )}
        </h1>
      </div>

      {/* Settlements */}
      <div className="px-6 sm:px-8 pt-6">
        <div className="label-eyebrow text-mute">{t('settle.whoOwes')}</div>
        <div className="mt-2.5">
          {!hasSettlements ? (
            <div className="py-3.5 border-t border-b border-line text-[15px] text-mute">
              {t('settle.none')}
            </div>
          ) : (
            transactions.map((tx, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-3.5"
                style={{
                  borderTop: i === 0 ? '1px solid #dfdbd3' : 'none',
                  borderBottom: '1px solid #dfdbd3',
                }}
              >
                <div className="text-[15px] tracking-[-0.01em]">
                  <span className="font-semibold">{tx.from}</span>
                  <span className="text-mute mx-1.5">→</span>
                  <span className="font-semibold">{tx.to}</span>
                </div>
                <div className="text-[16px] font-semibold tnum tracking-[-0.015em]">
                  {formatCurrency(tx.amount)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Rankings */}
      <div className="px-6 sm:px-8 pt-7">
        <div className="label-eyebrow text-mute">{t('settle.finalStacks')}</div>
        <div className="mt-2.5 flex flex-col gap-1.5">
          {sorted.map((player, i) => {
            const isWinner = !!(winner && player.id === winner.id);
            const netResult = player.netResult || 0;
            return (
              <div
                key={player.id || i}
                className={`flex items-center justify-between px-3.5 py-3 rounded-xl border ${
                  isWinner
                    ? 'bg-ink text-cream border-ink'
                    : 'bg-transparent text-ink border-line'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-[12px] font-medium tnum w-4"
                    style={{ opacity: isWinner ? 0.6 : 0.5 }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-[15.5px] font-semibold tracking-[-0.01em]">
                    {player.name}
                  </span>
                  {isWinner && (
                    <span
                      className="text-[9.5px] font-semibold tracking-[0.1em] px-1.5 py-0.5 rounded"
                      style={{ border: '1px solid rgba(255,255,255,0.35)', color: '#f6f4ef' }}
                    >
                      {t('settle.winner')}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-3 tnum">
                  <span
                    className="text-[13px] font-medium"
                    style={{
                      color: isWinner
                        ? 'rgba(246,244,239,0.65)'
                        : netResult < 0
                        ? '#8a857d'
                        : '#0d0d0d',
                    }}
                    data-testid={`net-${player.name}`}
                  >
                    {formatSignedCurrency(netResult, currency, locale)}
                  </span>
                  <span className="text-[16px] font-semibold tracking-[-0.015em]">
                    {formatCurrency(player.finalChips || 0)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick feedback — inline card, never a popup, so it can't block the
          settlement info people actually came to this screen for */}
      <FeedbackCard />

      {/* Footer — single button, no flex spacer */}
      <div className="px-6 sm:px-8 pt-8 pb-9 mt-8 border-t border-line">
        <button
          onClick={onBackToHome}
          className="w-full flex items-center justify-center py-3.5 rounded-full bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] hover:opacity-90 transition-opacity"
        >
          {t('settle.backHome')}
        </button>
      </div>

    </div>
  );
};

export default EndGameScreen;
